import ReactPlayer from "react-player";
import data from "~/data/appsInfo/SaangDhyan.json";
import data_gu from "~/data/appsInfo/SaangDhyan_gu.json";
import { useFontStyle } from "~/hook/useFontStyle";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { cn } from "~/lib/utils";
import { FALLBACK_IMAGE_ALT_TEXT, youtubeLinkPrefix } from "~/utils/constant";
import ClientOnly from "../shared-components/ClentOnly";

function SaangDhyan() {
  const lang = useLanguageParams();
  const { normalInter, medium } = useFontStyle();
  const dataToUse = lang === "gu" ? data_gu : data;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-start space-y-4">
        <h1 className={cn("h4_heading text-textColor", medium)}>
          {dataToUse?.emojis.main} {dataToUse?.subtitle} {dataToUse?.emojis.main}
        </h1>
        <p className={cn("body_text_m text-textSecondaryColor leading-relaxed", normalInter)}>
          {dataToUse?.description} {dataToUse?.emojis.meditation}
          {dataToUse?.emojis.sparkles}
        </p>
      </div>

      {/* App Video (if available) */}
      {dataToUse?.video && (
        <ClientOnly fallback={<div className="w-full aspect-video flex justify-center items-center bg-black/10 animate-pulse"></div>}>
          <div className="w-full aspect-video flex justify-center items-center">
            <ReactPlayer
              url={`${youtubeLinkPrefix}${dataToUse?.video}`}
              width="100%"
              height="100%"
              controls={true}
              style={{ pointerEvents: "auto" }}
            />
          </div>
        </ClientOnly>
      )}

      {/* What is Saang Dhyan */}
      <section className="rounded-lg">
        <h2 className={cn("h4_heading text-textColor", medium)}>
          {dataToUse?.emojis.flower} {dataToUse?.whatIs.title}
        </h2>
        <p className={cn("text-textSecondaryColor body_text_m leading-relaxed", normalInter)}>
          {dataToUse?.whatIs.content} {dataToUse?.emojis.magnifying} {dataToUse?.emojis.temple}
        </p>
      </section>

      {/* How This App Can Help */}
      <section>
        <h2 className={cn("h4_heading text-textColor", medium)}>
          {dataToUse?.emojis.rocket} {dataToUse?.howItHelps.title}
        </h2>
        <p className={cn("text-textSecondaryColor body_text_m mb-6", normalInter)}>
          {dataToUse?.howItHelps.content} {dataToUse?.emojis.heart}
        </p>
      </section>

      {/* Divine Options */}
      <section className="bg-cardBg rounded-lg p-6">
        <h2 className={cn("h4_heading text-textColor mb-6", medium)}>
          {dataToUse?.emojis.sparkles} {dataToUse?.divineOptions.title}
        </h2>
        <div className="space-y-4">
          {dataToUse?.divineOptions.options.map((option, index) => (
            <div key={index} className="border-l-4 border-borderPrimary pl-4">
              <h3 className={cn("body_text_l text-textColor mb-2", medium)}>
                {option.icon} {option.title}
              </h3>
              <p className={cn("text-textSecondaryColor body_text_m", normalInter)}>{option.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Advanced Interactive 3D Experience */}
      <section>
        <h2 className={cn("h4_heading text-textColor mb-6", medium)}>{dataToUse?.emojis.sparkles} Advanced Interactive 3D Experience</h2>

        {/* Features */}
        <div className="space-y-6">
          {dataToUse?.features.map((feature, index) => (
            <div key={index} className="border-l-4 border-borderPrimary pl-4">
              <h3 className={cn("body_text_l text-textColor mb-2", medium)}>
                {feature.icon} {feature.title}
              </h3>
              <p className={cn("text-textSecondaryColor body_text_m", normalInter)}>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Key Features */}
      <section className="bg-cardBg rounded-lg p-6">
        <h2 className={cn("h4_heading text-textColor mb-4", medium)}>{dataToUse?.emojis.sparkles} Key Features:</h2>
        <ul className="space-y-2">
          {dataToUse?.keyFeatures.map((feature, index) => (
            <li key={index} className="flex items-start space-x-2">
              <span className="text-textColor mt-1">•</span>
              <span className={cn("text-textSecondaryColor font-inter body_text_m", normalInter)}>{feature}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* How to Use */}
      <section className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-6">
        <h2 className={cn("h4_heading text-textColor mb-4", medium)}>
          {dataToUse?.emojis.rocket} {dataToUse?.steps.title}
        </h2>
        <ol className="space-y-2">
          {dataToUse?.steps.steps.map((step, index) => (
            <li key={index} className="flex items-start space-x-2">
              <span className={cn("text-textColor font-bold", normalInter)}>{index + 1}.</span>
              <span className={cn("text-textSecondaryColor body_text_m", normalInter)}>{step}</span>
            </li>
          ))}
        </ol>
      </section>

      {/* Who Is This App For */}
      <section>
        <h2 className={cn("h4_heading text-textColor mb-4", medium)}>
          {dataToUse?.emojis.prayer} {dataToUse?.whoIsThisFor.title}
        </h2>
        <ul className="space-y-2">
          {dataToUse?.whoIsThisFor.list.map((item, index) => (
            <li key={index} className="flex items-start space-x-2">
              <span className="text-textColor mt-1">•</span>
              <span className={cn("text-textSecondaryColor body_text_m", normalInter)}>{item}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Made with Devotion */}
      <section className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
        <h2 className={cn("h4_heading text-yellow-800 dark:text-yellow-200 mb-3", medium)}>
          {dataToUse?.emojis.heart} {dataToUse?.madeWithDevotion.title}
        </h2>
        <p className={cn("text-yellow-700 dark:text-yellow-300 body_text_m leading-relaxed", normalInter)}>
          {dataToUse?.madeWithDevotion.content} {dataToUse?.emojis.prayer} {dataToUse?.emojis.sparkles}
        </p>
      </section>

      {/* App Download Section */}
      <div className="w-full flex flex-col gap-4 items-center mb-5">
        {dataToUse?.icon && (
          <img
            src={dataToUse?.icon}
            title={dataToUse?.title || FALLBACK_IMAGE_ALT_TEXT}
            alt={dataToUse?.title || FALLBACK_IMAGE_ALT_TEXT}
            className="max-w-full h-auto rounded-3xl select-none"
          />
        )}
        <span className={cn("text-center font-medium text-textSecondaryColor", normalInter)}>{dataToUse?.downloadText}</span>
        <div className="flex gap-4">
          {dataToUse?.downloadLinks.playStore.url && (
            <a
              href={dataToUse?.downloadLinks.playStore.url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src={dataToUse?.downloadLinks.playStore.image}
                title={dataToUse?.downloadLinks.playStore.alt || FALLBACK_IMAGE_ALT_TEXT}
                alt={dataToUse?.downloadLinks.playStore.alt || FALLBACK_IMAGE_ALT_TEXT}
                className="select-none"
              />
            </a>
          )}
          {dataToUse?.downloadLinks.appStore.url && (
            <a href={dataToUse?.downloadLinks.appStore.url} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 transition-opacity">
              <img
                src={dataToUse?.downloadLinks.appStore.image}
                title={dataToUse?.downloadLinks.appStore.alt || FALLBACK_IMAGE_ALT_TEXT}
                alt={dataToUse?.downloadLinks.appStore.alt || FALLBACK_IMAGE_ALT_TEXT}
                className="select-none"
              />
            </a>
          )}
        </div>
        <p className={cn("text-center text-textColor body_text_l mt-4", medium)}>
          {dataToUse?.emojis.prayer} {dataToUse?.callToAction} {dataToUse?.emojis.prayer}
        </p>
      </div>
    </div>
  );
}

export default SaangDhyan;
